import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ProgramListItemDataFragment } from '../ProgramListing/ProgramListing.api';
import type { ProgramFormData } from './ProgramFormConfig';
import { defaultProgramFormValues, getFieldsToValidate, programFormSchema } from './ProgramFormConfig';
import { useProgramFormState, type FormMode } from './useProgramFormState';
import { ProgramOccurrence } from '@/apolloClient.types';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import InputField from '@/components/fields/InputField';
import RadioField from '@/components/fields/RadioField';
import SelectField from '@/components/fields/SelectField';
import TextareaField from '@/components/fields/TextareaField';
import { useFetchCoursesQuery } from '@/pages/GroupManagementPage/shared/Courses.api';

export interface ProgramFormProps {
    mode: FormMode;
    open: boolean;
    title: string;
    program?: ProgramListItemDataFragment | null;
    companyId?: string;
    onSubmit: (data: ProgramFormData) => Promise<void>;
    onClose: () => void;
}

const ProgramFormContainer = ({ mode, open, title, onSubmit, program, onClose, companyId }: ProgramFormProps) => {
    const { t } = useTranslation('programManagementPage');
    const { t: tGlobal } = useTranslation('common');

    const occurrenceOptions = [
        { value: ProgramOccurrence.NotRepeating, label: t('fields.occurrenceValue.NotRepeating') },
        { value: 'Standard', label: t('fields.occurrenceValue.Standard'), disabled: true },
        { value: ProgramOccurrence.Custom, label: t('fields.occurrenceValue.Custom') },
    ];

    const { data } = useFetchCoursesQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            companyId: companyId || '',
        },
        skip: !open || !companyId || mode === 'edit',
    });

    const coursesOptions = useMemo(() => {
        const coursesOptions = (data?.courseInstances?.items || []).map(course => ({
            value: course.id,
            label: course.name,
        }));

        return coursesOptions;
    }, [data?.courseInstances.items]);

    // Initialize form state
    const initialData = useMemo(() => {
        return program
            ? {
                  name: program.name || '',
                  description: program.description || '',
                  courseInstanceId: program.courseInstanceId || '',
                  occurrence: program.occurrence || ProgramOccurrence.NotRepeating,
              }
            : undefined;
    }, [program]);

    const { state, actions } = useProgramFormState(mode, initialData);
    const form = useForm<ProgramFormData>({
        mode: 'onChange',
        resolver: zodResolver(programFormSchema),
        defaultValues: state.formData,
    });

    useEffect(() => {
        if (companyId && open) {
            actions.initialize(initialData || defaultProgramFormValues);
            form.reset(initialData);
        }
    }, [companyId, open, actions, form, initialData]);

    const handleClose = useCallback(() => {
        form.reset(defaultProgramFormValues);
        actions.reset();
        onClose();
    }, [onClose, actions, form]);

    const handleSubmit = useCallback(async () => {
        const fieldsToValidate = getFieldsToValidate();
        const isValid = await form.trigger(fieldsToValidate);
        if (!isValid) {
            return;
        }
        const formData = form.getValues();
        try {
            actions.submitStart();
            await onSubmit(formData);
            actions.submitSuccess();
            handleClose();
        } catch (error) {
            actions.submitError();
            if (error && typeof error === 'object' && 'fields' in error) {
                const fields = error.fields as { field: string; message: string }[];
                console.error('Validation errors:', fields);
                fields.forEach(err => {
                    if (err.field && err.message) {
                        form.setError(err.field as keyof ProgramFormData, {
                            type: 'manual',
                            message: err.message,
                        });
                    }
                });
            }
        }
    }, [form, onSubmit, actions, handleClose]);

    const modalFooter = (
        <div className="flex items-center justify-end gap-2.5">
            <Button outline onClick={handleClose} disabled={state.isSubmitting} compact>
                {tGlobal('modal.cancel')}
            </Button>
            <Button color="teal" type="submit" onClick={handleSubmit} disabled={state.isSubmitting} compact>
                {mode === 'edit' ? t('modalEditProgram.updateButton') : t('modalCreateProgram.createButton')}
            </Button>
        </div>
    );

    const modalWidth = mode === 'edit' ? '!w-[512px] !max-w-[512px]' : '!w-[800px] !max-w-[800px]';

    return (
        <FormProvider {...form}>
            <Modal
                open={open}
                onClose={handleClose}
                title={title}
                largeTitle
                subtitle={mode !== 'edit' ? t('modalCreateProgram.subtitle') : undefined}
                subtitleClassName="mt-1"
                footer={modalFooter}
                showCloseButton={false}
                className={modalWidth}
            >
                <div className="flex flex-col">
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="mb-5 flex flex-col gap-5">
                        <InputField
                            label={t('fields.name.label')}
                            name="name"
                            placeholder={t('fields.name.placeholder')}
                            required
                            disabled={false}
                            labelSize="base"
                            labelWeight="semibold"
                        />
                        <TextareaField
                            label={t('fields.description.label')}
                            name="description"
                            placeholder={t('fields.description.placeholder')}
                            rows={4}
                            labelSize="base"
                            labelWeight="semibold"
                        />
                        {mode !== 'edit' && (
                            <>
                                <SelectField
                                    name="courseInstanceId"
                                    label={t('fields.courses.label')}
                                    options={coursesOptions}
                                    required
                                    placeholder={t('fields.courses.placeholder')}
                                    labelSize="base"
                                    labelWeight="semibold"
                                />
                                <RadioField
                                    name="occurrence"
                                    label={t('fields.occurrence.label')}
                                    options={occurrenceOptions}
                                    defaultValue={ProgramOccurrence.NotRepeating}
                                    required
                                    variant="modern"
                                    labelSize="base"
                                    labelWeight="semibold"
                                />
                            </>
                        )}
                    </form>
                </div>
            </Modal>
        </FormProvider>
    );
};

export default ProgramFormContainer;
